import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  de: {
    // Navigation
    dashboard: 'Dashboard',
    accounts: 'Konten',
    settings: 'Einstellungen',
    subscription: 'Abonnement',
    logout: 'Abmelden',
    
    // Dashboard
    tradingJournal: 'Trading Journal',
    evaluationFor: 'Auswertung für',
    import: 'Import',
    all: 'Alle',
    backtest: 'Backtest',
    csv: 'CSV',
    screenshots: 'Screenshots',
    
    // Stats
    twrPercent: 'TWR %',
    winRate: 'Gewinnrate',
    profitFactor: 'Profit Faktor',
    detailedEvaluation: 'Detaillierte Auswertung',
    totalTrades: 'Gesamt Trades',
    winnersLosers: 'Gewinner / Verlierer',
    totalPnl: 'Gesamt PnL',
    avgPnlPerTrade: 'Durchschn. PnL pro Trade',
    bestTrade: 'Bester Trade',
    worstTrade: 'Schlechtester Trade',
    avgWin: 'Durchschn. Gewinn',
    maxDrawdown: 'Max Drawdown',
    rrRatio: 'R:R Ratio',
    
    // Calendar
    tradingCalendar: 'Trading-Kalender',
    monday: 'Mo',
    tuesday: 'Di',
    wednesday: 'Mi',
    thursday: 'Do',
    friday: 'Fr',
    saturday: 'Sa',
    sunday: 'So',
    
    // Performance
    performance: 'Performance',
    
    // Auth
    login: 'Anmelden',
    register: 'Registrieren',
    email: 'E-Mail',
    password: 'Passwort',
    name: 'Name',
    continueWithGoogle: 'Mit Google fortfahren',
    or: 'oder',
    alreadyHaveAccount: 'Bereits ein Konto?',
    dontHaveAccount: 'Noch kein Konto?',
    
    // Import
    importTrades: 'Trades importieren',
    dragDropCsv: 'CSV-Datei hierher ziehen oder klicken',
    selectAccount: 'Konto auswählen',
    importButton: 'Importieren',
    cancel: 'Abbrechen',
    tradesImported: 'Trades importiert',
    
    // Accounts
    createAccount: 'Konto erstellen',
    accountName: 'Kontoname',
    broker: 'Broker',
    currency: 'Währung',
    delete: 'Löschen',
    
    // Subscription
    choosePlan: 'Wählen Sie Ihren Plan',
    monthly: 'Monatlich',
    basic: 'Basic',
    pro: 'Pro',
    premium: 'Premium',
    oneAccount: '1 Trading-Konto',
    fiveAccounts: '5 Trading-Konten',
    unlimitedAccounts: 'Unbegrenzte Konten',
    allFeatures: 'Alle Features',
    prioritySupport: 'Prioritäts-Support',
    selectPlan: 'Plan auswählen',
    currentPlan: 'Aktueller Plan',
    
    // Emotions
    howDoYouFeel: 'Wie fühlst du dich heute?',
    addNote: 'Notiz hinzufügen (optional)',
    save: 'Speichern',
    
    // Settings
    language: 'Sprache',
    german: 'Deutsch',
    english: 'English',
    saveSettings: 'Einstellungen speichern',
    
    // Landing
    heroTitle: 'Dein Trading Journal für maximale Performance',
    heroSubtitle: 'Analysiere deine Trades, erkenne Muster und steigere deine Profitabilität.',
    getStarted: 'Jetzt starten',
    features: 'Features',
    pricing: 'Preise',
    
    // Months
    months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    accounts: 'Accounts',
    settings: 'Settings',
    subscription: 'Subscription',
    logout: 'Logout',
    
    // Dashboard
    tradingJournal: 'Trading Journal',
    evaluationFor: 'Evaluation for',
    import: 'Import',
    all: 'All',
    backtest: 'Backtest',
    csv: 'CSV',
    screenshots: 'Screenshots',
    
    // Stats
    twrPercent: 'TWR %',
    winRate: 'Win Rate',
    profitFactor: 'Profit Factor',
    detailedEvaluation: 'Detailed Evaluation',
    totalTrades: 'Total Trades',
    winnersLosers: 'Winners / Losers',
    totalPnl: 'Total PnL',
    avgPnlPerTrade: 'Avg. PnL per Trade',
    bestTrade: 'Best Trade',
    worstTrade: 'Worst Trade',
    avgWin: 'Avg. Win',
    maxDrawdown: 'Max Drawdown',
    rrRatio: 'R:R Ratio',
    
    // Calendar
    tradingCalendar: 'Trading Calendar',
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
    
    // Performance
    performance: 'Performance',
    
    // Auth
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    continueWithGoogle: 'Continue with Google',
    or: 'or',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    
    // Import
    importTrades: 'Import Trades',
    dragDropCsv: 'Drag and drop CSV file here or click',
    selectAccount: 'Select Account',
    importButton: 'Import',
    cancel: 'Cancel',
    tradesImported: 'trades imported',
    
    // Accounts
    createAccount: 'Create Account',
    accountName: 'Account Name',
    broker: 'Broker',
    currency: 'Currency',
    delete: 'Delete',
    
    // Subscription
    choosePlan: 'Choose Your Plan',
    monthly: 'Monthly',
    basic: 'Basic',
    pro: 'Pro',
    premium: 'Premium',
    oneAccount: '1 Trading Account',
    fiveAccounts: '5 Trading Accounts',
    unlimitedAccounts: 'Unlimited Accounts',
    allFeatures: 'All Features',
    prioritySupport: 'Priority Support',
    selectPlan: 'Select Plan',
    currentPlan: 'Current Plan',
    
    // Emotions
    howDoYouFeel: 'How do you feel today?',
    addNote: 'Add note (optional)',
    save: 'Save',
    
    // Settings
    language: 'Language',
    german: 'Deutsch',
    english: 'English',
    saveSettings: 'Save Settings',
    
    // Landing
    heroTitle: 'Your Trading Journal for Maximum Performance',
    heroSubtitle: 'Analyze your trades, recognize patterns and increase your profitability.',
    getStarted: 'Get Started',
    features: 'Features',
    pricing: 'Pricing',
    
    // Months
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'de';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['de'][key] || key;
  };

  const getMonth = (monthIndex) => {
    return translations[language]?.months[monthIndex] || translations['de'].months[monthIndex];
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getMonth }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
